import { createAction, createReducer } from "@reduxjs/toolkit";
import { get, isEmpty } from "lodash-es";
import { CART_LIMIT, DEFAULT_FILTER_DATA } from "../../constants/diy";
import {
  getCategoryData,
  getSubcategories,
  getSubcategoryData,
  getSubcategoryOptions,
  checkFundPresentInCart,
  getFilterFundsDataByCategory,
  getSubcategoryDataByViewType,
} from "../../utils/diy/functions";

const Actions = {
  fetchDiyCategories: "fetchDiyCategories",
  setDiyCategories: "setDiyCategories",
  fetchTrendingFunds: "fetchTrendingFunds",
  setTrendingFunds: "setTrendingFunds",
  fetchFundList: "fetchFundList",
  setFundList: "setFundList",
  setFundHouses: "setFundHouses",
  fetchDiyCategoriesAndTrendingFunds: "fetchDiyCategoriesAndTrendingFunds",
  setFilteredFundList: "setFilteredFundList",
  setDiyStorage: "setDiyStorage",
  setDiyTypeData: "setDiyTypeData",
  setCartItem: "setCartItem",
  setFundsCart: "setFundsCart",
  resetDiyData: "resetDiyData",
  setDiySeeMore: "setDiySeeMore",
  getSearchFunds: "getSearchFunds",
  setSearchFunds: "setSearchFunds",
  setSelectedFundDetails: "setSelectedFundDetails",
  resetFilters: "resetFilters",
  getDiyDataForFundListDeeplink: "getDiyDataForFundListDeeplink",
};

// Actions
export const fetchDiyCategories = createAction(Actions.fetchDiyCategories);
export const setDiyCategories = createAction(Actions.setDiyCategories);
export const fetchTrendingFunds = createAction(Actions.fetchTrendingFunds);
export const setTrendingFunds = createAction(Actions.setTrendingFunds);
export const fetchFundList = createAction(Actions.fetchFundList);
export const setFundList = createAction(Actions.setFundList);
export const setFundHouses = createAction(Actions.setFundHouses);
export const setFilteredFundList = createAction(Actions.setFilteredFundList);
export const setDiyStorage = createAction(Actions.setDiyStorage);
export const setDiyTypeData = createAction(Actions.setDiyTypeData);
export const setFundsCart = createAction(Actions.setFundsCart);
export const resetDiyData = createAction(Actions.resetDiyData);
export const setDiySeeMore = createAction(Actions.setDiySeeMore);
export const getSearchFunds = createAction(Actions.getSearchFunds);
export const setSearchFunds = createAction(Actions.setSearchFunds);
export const getDiyDataForFundListDeeplink = createAction(
  Actions.getDiyDataForFundListDeeplink
);

export const setSelectedFundDetails = createAction(
  Actions.setSelectedFundDetails
);
export const fetchDiyCategoriesAndTrendingFunds = createAction(
  Actions.fetchDiyCategoriesAndTrendingFunds
);
export const setCartItem = createAction(Actions.setCartItem);
export const resetFilters = createAction(Actions.resetFilters);

export const getAllCategories = (state) => {
  return get(state, "diy.categories", []);
};

export const getDiyCategoryData = (state, category) => {
  const categories = get(state, "diy.categories", []);
  return getCategoryData(categories, category);
};

export const getDiySubcategories = (state, category) => {
  const categories = get(state, "diy.categories", []);
  return getSubcategories(categories, category);
};

export const getDiySubcategoryData = (state, category, subcategory) => {
  const categories = get(state, "diy.categories", []);
  return getSubcategoryData(categories, category, subcategory);
};

export const getDiySubcategoryDataByViewType = (state, category, viewType) => {
  const categories = get(state, "diy.categories", []);
  return getSubcategoryDataByViewType(categories, category, viewType);
};

export const getDiySubcategoryOptions = (state, category, subcategory) => {
  const categories = get(state, "diy.categories", []);
  return getSubcategoryOptions(categories, category, subcategory) || {};
};

export const getTrendingFunds = (state) => {
  return get(state, "diy.trendingFunds", []);
};

export const getTrendingFundsByCategory = (state, category) => {
  const trendingFunds = get(state, "diy.trendingFunds", []);
  return trendingFunds[category] || [];
};

export const getFundsList = (state) => {
  return get(state, "diy.fundListData", {});
};

export const getDiyStorage = (state) => {
  return get(state, "diy.diyStorage", {});
};

export const getDiyTypeData = (state) => {
  return get(state, "diy.diyTypeData", {});
};

export const getFundsByCategory = (state, category) => {
  const fundsList = get(state, "diy.fundListData", {});
  return fundsList[category] || {};
};

export const getFundsBySubcategory = (state, category, subcategory) => {
  const fundsList = get(state, "diy.fundListData", {});
  const fundsListByCategory = fundsList[category] || {};
  return fundsListByCategory[subcategory] || [];
};

export const getFilteredFundsByCategory = (state, category) => {
  const filteredFundsList = get(state, "diy.filteredData.fundList", {});
  const filteredFundsByCategory = filteredFundsList[category] || {};
  return filteredFundsByCategory || [];
};

export const getFilterOptions = (state) => {
  return get(state, "diy.filteredData.filterOptions", {});
};

export const getFundHouses = (state) => {
  return get(state, "diy.fundHouses", []);
};

export const getDiyCart = (state) => {
  const funds = get(state, "diy.fundsCart", []);
  return funds || [];
};

export const getDiyCartCount = (state) => {
  const count = get(state, "diy.cartItemCount", 0);
  return count;
};

const initialState = {
  categories: [],
  trendingFunds: [],
  fundListData: {},
  fundHouses: [],
  filteredData: {
    filterOptions: DEFAULT_FILTER_DATA,
    fundList: {},
  },
  diyTypeData: {
    category: "",
    subcategory: "",
    subcategoryOption: "",
  },
  diyStorage: {},
  fundsCart: [],
  cartItemCount: 0,
  isSeeMoreClicked: false,
};

export default createReducer(initialState, (builder) => {
  builder.addCase(setDiyCategories, (state, action) => {
    state.categories = action.payload;
  });
  builder.addCase(setTrendingFunds, (state, action) => {
    state.trendingFunds = action.payload;
  });
  builder.addCase(setFundHouses, (state, action) => {
    state.fundHouses = action.payload;
  });
  builder.addCase(setDiyStorage, (state, action) => {
    state.diyStorage = { ...state.diyStorage, ...action.payload };
  });
  builder.addCase(setDiyTypeData, (state, action) => {
    state.diyTypeData = { ...state.diyTypeData, ...action.payload };
  });
  builder.addCase(setFundList, (state, action) => {
    const { fundList, category, subcategoryOption } = action.payload;
    if (state.fundListData[category]) {
      state.fundListData[category][subcategoryOption] = fundList;
    } else {
      state.fundListData[category] = { [subcategoryOption]: fundList };
    }
  });
  builder.addCase(setFilteredFundList, (state, action) => {
    let { category, subcategory, filterOptions = {} } = action.payload;
    let fundsListByCategory = state.fundListData[category] || {};
    let prevdata = { ...state.filteredData };
    if (isEmpty(filterOptions)) {
      filterOptions = prevdata.filterOptions;
    } else {
      prevdata.filterOptions = filterOptions;
    }
    const options = getSubcategoryOptions(
      state.categories,
      category,
      subcategory
    );
    fundsListByCategory = getFilterFundsDataByCategory(
      fundsListByCategory,
      filterOptions,
      options
    );
    if (prevdata.fundList[category]) {
      prevdata.fundList[category] = fundsListByCategory;
      state.filteredData = prevdata;
    } else {
      state.filteredData.fundList[category] = fundsListByCategory;
    }
  });
  builder.addCase(setCartItem, (state, action) => {
    let isPresent = checkFundPresentInCart(state.fundsCart, action.payload);
    if (!isPresent && state.fundsCart.length < CART_LIMIT) {
      state.fundsCart = [...state.fundsCart, action.payload];
    } else {
      let fundsCart = state.fundsCart.filter(
        (item) => item.isin !== action.payload.isin
      );
      state.fundsCart = fundsCart;
    }
    state.cartItemCount = state.fundsCart.length;
  });
  builder.addCase(setFundsCart, (state, action) => {
    state.fundsCart = action.payload;
    state.cartItemCount = action.payload.length || 0;
  });
  builder.addCase(resetDiyData, (state) => {
    return initialState;
  });
  builder.addCase(setDiySeeMore, (state, action) => {
    state.isSeeMoreClicked = action.payload;
  });
  builder.addCase(setSearchFunds, (state, action) => {
    state.searchResults = action.payload;
  });
  builder.addCase(setSelectedFundDetails, (state, action) => {
    state.selectedFundDetails = action.payload;
  });
  builder.addCase(resetFilters, (state) => {
    state.filteredData.filterOptions = DEFAULT_FILTER_DATA;
    state.filteredData.fundList = {};
  });
});
